import React, { Fragment, useEffect, useMemo, useState } from "react"
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  useFilters,
  usePagination,
  useAsyncDebounce,
} from "react-table"

import { Table, Row, Col, Button, Input } from "reactstrap"
import {
  Filter,
  DefaultColumnFilter,
  SelectColumnFilter,
  GlobalFilter,
} from "./Filters"
import LoadingSpinner from "../LoadingSpinner"
import { TableRangeFooterLabel } from "./TableRangeFooterLabel"

const CustomTable = ({
  data = [],
  columns = [],
  totalRecords = 0,
  stateAPI = { limit: 10, type: "", page: 1, search: "", status: undefined },
  setStateAPI = () => {},
  list = { isFetching: false, isLoading: false },
  userId = null,
  hideColumns = [],
  createButton = {
    visible: false,
    lable: "Create New",
    onClick: () => {},
  },
}) => {
  const [pSize, setPSize] = useState(10)
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    visibleColumns,
    setHiddenColumns,
    state: { pageIndex, pageSize, globalFilter, filters },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        // type: pSize.type,
        sortBy: [
          {
            desc: true,
            id: "id",
          },
        ],
        hiddenColumns: userId ? ["id", "user.name", "_id"] : ["id", "_id"],
      },
      autoResetFilters: false,
      autoResetGlobalFilter: false,
      autoResetSortBy: false,
      manualPagination: true,
      manualSortBy: true,
      manualFilters: true,
      autoResetPage: false,
      pageCount: Math.ceil((totalRecords || 10) / pSize),
    },
    useFilters,
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
  )
  useEffect(() => {
    if (hideColumns.length) {
      setHiddenColumns(state => {
        const filters = state.filter(column => column)
        return [...filters, ...hideColumns]
      })
    }
  }, [hideColumns])
  useEffect(() => {
    const categoryFilter =
      state?.filters.find(filter => filter.id === "categories") ||
      state?.filters.find(filter => filter.id === "type")

    const isLiveFilter =
      state?.filters.find(filter => filter.id === "isLive") ||
      state?.filters.find(filter => filter.id === "status")

    const newType = categoryFilter ? categoryFilter.value : null
    const newStatus = isLiveFilter ? isLiveFilter.value : null

    const isDifferent =
      state?.globalFilter !== stateAPI.search ||
      newType !== stateAPI.type ||
      newStatus !== stateAPI.status

    const updatedStateAPI = {
      ...stateAPI,
      search: state?.globalFilter,
      type: newType,
      status: newStatus,
      page: isDifferent ? 1 : state.pageIndex + 1,
      limit: state.pageSize,
    }

    if (isDifferent) {
      gotoPage(0)
    }

    setStateAPI(updatedStateAPI)
  }, [state?.filters, state.pageIndex, state.pageSize, state?.globalFilter])
  const generateSortingIndicator = column => {
    return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""
  }

  const onChangeInPageSize = event => {
    setPageSize(Number(event.target.value))
    setPSize(Number(event.target.value))
  }
  const onGoToPage = useAsyncDebounce(event => {
    const page = event.target.value ? Number(event.target.value - 1) : 0
    gotoPage(page)
  }, 500)
  // const onGoToPage = event => {
  //   const page = event.target.value ? Number(event.target.value - 1) : 0
  //   gotoPage(page)
  // }
  useEffect(() => {
    if (
      !list.isLoading &&
      !list.isFetching &&
      !data.length &&
      !totalRecords &&
      pageIndex - 1 > 0
    ) {
      gotoPage(pageIndex - 1)
    }
  }, [data])
  return (
    <Fragment>
      <Row className="mb-2">
        <Col md={2}>
          <select
            className="form-select"
            value={pageSize}
            onChange={onChangeInPageSize}
          >
            {[10, 20, 30, 40, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </Col>
        {createButton?.visible && (
          <Col>
            <div className="text-sm-end">
              <Button
                type="button"
                color="primary"
                className="btn mb-2 me-2"
                onClick={() => createButton.onClick()}
              >
                <i className="mdi mdi-plus-circle-outline me-1" />
                {createButton.lable}
              </Button>
            </div>
          </Col>
        )}
      </Row>

      <div className="table-responsive react-table">
        <Table bordered hover {...getTableProps()}>
          <thead className="table-light table-nowrap">
            <tr>
              <th
                colSpan={visibleColumns.length}
                style={{
                  textAlign: "left",
                }}
              >
                <GlobalFilter
                  preGlobalFilteredRows={preGlobalFilteredRows}
                  globalFilter={globalFilter}
                  setGlobalFilter={setGlobalFilter}
                />
              </th>
            </tr>

            {headerGroups.map(headerGroup => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    key={column.id}
                    style={
                      column.maxSize ? { width: `${column.maxSize}px` } : {}
                    }
                  >
                    <div className="mb-2" {...column.getSortByToggleProps()}>
                      {column.render("Header")}
                      {generateSortingIndicator(column)}
                    </div>
                    <Filter column={column} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {(list?.isFetching || list?.isLoading) && (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  style={{
                    textAlign: "center",
                  }}
                >
                  <LoadingSpinner />
                </td>
              </tr>
            )}

            {!page?.length && !list?.isFetching && !list?.isLoading && (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  style={{
                    textAlign: "center",
                  }}
                >
                  <p>
                    {" "}
                    Data Not Found{" "}
                    <span
                      style={{
                        fontSize: "100px;",
                      }}
                    >
                      &#128581;
                    </span>
                  </p>
                </td>
              </tr>
            )}

            {!list?.isFetching &&
              !list?.isLoading &&
              page.map(row => {
                prepareRow(row)
                return (
                  <Fragment key={row.getRowProps().key}>
                    <tr>
                      {row.cells.map(cell => {
                        return (
                          <td key={cell.id} {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        )
                      })}
                    </tr>
                  </Fragment>
                )
              })}
          </tbody>
        </Table>
      </div>

      {totalRecords ? (
        <Row className="justify-content-md-end justify-content-center align-items-center">
          <Col className="col justify-content-start">
            <TableRangeFooterLabel
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalRecords={totalRecords}
            />
          </Col>
          <Col className="col-md-auto">
            <div className="d-flex gap-1">
              <Button
                color="primary"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                {"<<"}
              </Button>
              <Button
                color="primary"
                onClick={previousPage}
                disabled={!canPreviousPage}
              >
                {"<"}
              </Button>
            </div>
          </Col>
          <Col className="col-md-auto d-none d-md-block">
            Page{" "}
            <strong>
              {pageIndex + 1} of {pageOptions.length}
            </strong>
          </Col>
          <Col className="col-md-auto">
            <Input
              type="number"
              min={1}
              style={{ width: 70 }}
              max={pageOptions.length}
              defaultValue={pageIndex + 1}
              onChange={onGoToPage}
            />
          </Col>
          <Col className="col-md-auto">
            <div className="d-flex gap-1">
              <Button
                color="primary"
                onClick={nextPage}
                disabled={!canNextPage}
              >
                {">"}
              </Button>
              <Button
                color="primary"
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              >
                {">>"}
              </Button>
            </div>
          </Col>
        </Row>
      ) : (
        <></>
      )}
    </Fragment>
  )
}
export default CustomTable
